import { register } from "../actions/register";
import { FONT_FAMILY, VERTICAL_ALIGN } from "../constants";
import { t } from "../i18n";
import { ExcalidrawProps } from "../types";
import { getFontString, updateActiveTool } from "../utils";
import { setCursorForShape } from "../cursor";
import { newTextElement } from "./newElement";
import { getContainerElement, wrapText } from "./textElement";
import {
  isFrameLikeElement,
  isIframeElement,
  isIframeLikeElement,
} from "./typeChecks";
import {
  ExcalidrawElement,
  ExcalidrawIframeLikeElement,
  IframeData,
  NonDeletedExcalidrawElement,
} from "./types";
import {generateUUID2, getUrlQueryParam} from "../../../../src/utils";
import {isWeb} from "../../../../src/electron_interactive";
import {KEY_GLOBAL_YOUTUBE_EMBED_MODE} from "../../../../src/Const";

const embeddedLinkCache = new Map<string, IframeData>();

const RE_YOUTUBE =
    /^(?:http(?:s)?:\/\/)?(?:www\.)?youtu(?:be\.com|\.be)\/(embed\/|watch\?v=|shorts\/|playlist\?list=|embed\/videoseries\?list=)?([a-zA-Z0-9_-]+)(?:\?t=|&t=|\?start=|&start=)?([a-zA-Z0-9_-]+)?[^\s]*$/;

const RE_VIMEO =
    /^(?:http(?:s)?:\/\/)?(?:(?:w){3}.)?(?:player\.)?vimeo\.com\/(?:video\/)?([^?\s]+)(?:\?.*)?$/;
const RE_FIGMA = /^https:\/\/(?:www\.)?figma\.com/;

const RE_GH_GIST = /^https:\/\/gist\.github\.com/;
const RE_GH_GIST_EMBED =
    /^<script[\s\S]*?\ssrc=["'](https:\/\/gist.github.com\/.*?)\.js["']/i;

// not anchored to start to allow <blockquote> twitter embeds
const RE_TWITTER = /(?:http(?:s)?:\/\/)?(?:(?:w){3}.)?(?:twitter|x).com/;
const RE_TWITTER_EMBED =
    /^<blockquote[\s\S]*?\shref=["'](https:\/\/(?:twitter|x).com\/[^"']*)/i;

const RE_VALTOWN =
    /^https:\/\/(?:www\.)?val.town\/(v|embed)\/[a-zA-Z_$][0-9a-zA-Z_$]+\.[a-zA-Z_$][0-9a-zA-Z_$]+/;

const RE_GENERIC_EMBED =
    /^<(?:iframe|blockquote)[\s\S]*?\s(?:src|href)=["']([^"']*)["'][\s\S]*?>$/i;

const RE_GIPHY =
    /giphy.com\/(?:clips|embed|gifs)\/[a-zA-Z0-9]*?-?([a-zA-Z0-9]+)(?:[^a-zA-Z0-9]|$)/;

const RE_GOOGLE_DRIVE = /\/file\/d\/([-"\w]+)/;

const RE_LOOM = /^https:\/\/www.loom.com\/share/;

const RE_DROP_BOX = /^https:\/\/www.dropbox.com/;

const RE_TIK_TOK = /^https:\/\/www.tiktok.com/;
const TIK_TOK_EMBED = /video\/(\d+)/;
const RE_BILIBILI = /^https:\/\/www.bilibili.com/;

export const RE_IFRAME = "<iframe";
export const RE_IFRAME_REGEX = /src="([^"]*)"|src='([^']*)'/;


const ALLOWED_DOMAINS = new Set([
  "**",
  // "youtube.com",
  // "youtu.be",
  // "vimeo.com",
  // "player.vimeo.com",
  // "figma.com",
  // "link.excalidraw.com",
  // "gist.github.com",
  // "twitter.com",
  // "x.com",
  // "*.simplepdf.eu",
  // "stackblitz.com",
  // "val.town",
  // "giphy.com",
  // "dddice.com",
]);

export const createSrcDoc = (body: string) => {
  return `<html><body>${body}</body></html>`;
};

export const extractBVCode = (url: string) => {
  // 正则表达式匹配Bilibili视频链接中的BV号
  const bvRegex = /BV([a-zA-Z0-9]{10})/;
  const match = url.match(bvRegex);
  return match ? match[1] : null; // 如果匹配到，则返回BV号，否则返回null
};

// 转换输入的网站成可访问的形式（不带安全策略的）
export const getEmbedLink = (
    link: string | null | undefined,
    time:any=0
): IframeData | null => {
  if (!link) {
    return null;
  }

  // if (embeddedLinkCache.has(link)) {
  //   return embeddedLinkCache.get(link)!;
  // }

  const originalLink = link;

  let type: "video" | "generic" = "generic";
  let aspectRatio = { w: 840, h: 560 };

  if (link.startsWith(RE_IFRAME)) {
    const match = link.match(RE_IFRAME_REGEX);
    link = match![1] || match![2];

    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }
  if (RE_TIK_TOK.test(link)) {
    const tiktokMatch = link.match(TIK_TOK_EMBED);
    link = `https://www.tiktok.com/embed/v3/${tiktokMatch![1]}`;
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }
  if (RE_BILIBILI.test(link)) {
    const bvid = extractBVCode(link);
    const p = getUrlQueryParam(link,'p');
    if (bvid) {
      // link = `https://player.bilibili.com/player.html?bvid=${bvid}&t=5&autoplay=false`+(p?`&p=${p}`:'');
      link = `https://www.bilibili.com/blackboard/html5player.html?bvid=${bvid}&t=${time}&high_quality=1&autoplay=0`+(p?`&p=${p}`:'');
      if (isWeb()){//web使用iframe
        link = `https://player.bilibili.com/player.html?bvid=${bvid}&t=${time || 0}&autoplay=false`+(p?`&p=${p}`:'');
      }
      embeddedLinkCache.set(originalLink, {
        link,
        intrinsicSize: aspectRatio,
        type,
      });
      return { link, intrinsicSize: aspectRatio, type };
    }
  }
  if (RE_DROP_BOX.test(link)) {
    link = link.replace(/(\?|&)dl=[^&]*/, (match, prefix) => {
      // 如果匹配项前面有?，则保留?；如果有&，则替换为&
      return `${prefix}raw=1`;
    });
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }

  if (RE_LOOM.test(link)) {
    link = link.replace(/\/share\//, "/embed/");
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }

  const googleDrive = link.match(RE_GOOGLE_DRIVE);
  const fileId = googleDrive?.[1];
  if (fileId) {
    link = `https://drive.google.com/file/d/${fileId}/preview`;
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }

  const ytLink = link.match(RE_YOUTUBE);
  const szYoutubeMode = localStorage.getItem(KEY_GLOBAL_YOUTUBE_EMBED_MODE) || 'default';
  let szYoutubeUrl = 'youtube';
  if (szYoutubeMode != 'default'){
    szYoutubeUrl = 'youtube-nocookie'
  }
  const origin = '&origin=https://www.noteey.com/'
  const widgetid = '&widgetid=1'
  const forigin = '&forigin=https://www.noteey.com/'+generateUUID2()+'/whiteboard/tab/'+generateUUID2();
  const testhep = '&origin=https://app.heptabase.com&widgetid=1&forigin=https://app.heptabase.com/f0c36d36-4a6f-4785-918b-3a350aa1fa90/whiteboard/2cc61946-f8da-4e1b-94d0-c8e41270f022/tab/b35df71d-d132-493c-aeb4-9e508224805e&aoriginsup=1&gporigin=https://project-meta-6138f.firebaseapp.com/&vf=6'

  if (ytLink?.[2]) {
    const time = ytLink[3] ? `&start=${ytLink[3]}` : ``;
    const isPortrait = link.includes("shorts");
    type = "video";
    switch (ytLink[1]) {
      case "embed/":
      case "watch?v=":
      case "shorts/":
        link = `https://www.${szYoutubeUrl}.com/embed/${ytLink[2]}?enablejsapi=1${time}`;
        break;
      case "playlist?list=":
      case "embed/videoseries?list=":
        link = `https://www.${szYoutubeUrl}.com/embed/videoseries?list=${ytLink[2]}&enablejsapi=1${time}`;
        break;
      default:
        link = `https://www.${szYoutubeUrl}.com/embed/${ytLink[2]}?enablejsapi=1${time}`;
        break;
    }
    aspectRatio = isPortrait ? { w: 315, h: 560 } : { w: 560, h: 315 };
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
      videoId: ytLink[2],
    });
    return { link, intrinsicSize: aspectRatio, type ,videoId: ytLink[2]};
  }

  const vimeoLink = link.match(RE_VIMEO);
  if (vimeoLink?.[1]) {
    const target = vimeoLink?.[1];
    const warning = !/^\d+$/.test(target)
        ? t("toast.unrecognizedLinkFormat")
        : undefined;
    type = "video";
    link = `https://player.vimeo.com/video/${target}?api=1`;
    aspectRatio = { w: 560, h: 315 };
    //warning deliberately ommited so it is displayed only once per link
    //same link next time will be served from cache
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type, warning };
  }

  const figmaLink = link.match(RE_FIGMA);
  if (figmaLink) {
    type = "generic";
    link = `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(
        link,
    )}`;
    aspectRatio = { w: 550, h: 550 };
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }

  const valLink = link.match(RE_VALTOWN);
  if (valLink) {
    link =
        valLink[1] === "embed" ? valLink[0] : valLink[0].replace("/v", "/embed");
    embeddedLinkCache.set(originalLink, {
      link,
      intrinsicSize: aspectRatio,
      type,
    });
    return { link, intrinsicSize: aspectRatio, type };
  }

  if (RE_TWITTER.test(link)) {
    // the embed srcdoc still supports twitter.com domain only
    link = link.replace(/\bx.com\b/, "twitter.com");

    let ret: IframeData;
    // assume embed code
    if (/<blockquote/.test(link)) {
      const srcDoc = createSrcDoc(link);
      ret = {
        type: "document",
        srcdoc: () => srcDoc,
        intrinsicSize: { w: 480, h: 480 },
      };
      // assume regular tweet url
    } else {
      ret = {
        type: "document",
        srcdoc: (theme: string) =>
            createSrcDoc(
                `<blockquote class="twitter-tweet" data-dnt="true" data-theme="${theme}"><a href="${link}"></a></blockquote> <script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>`,
            ),
        intrinsicSize: { w: 480, h: 480 },
      };
    }
    embeddedLinkCache.set(originalLink, ret);
    return ret;
  }

  if (RE_GH_GIST.test(link)) {
    let ret: IframeData;
    // assume embed code
    if (/<script>/.test(link)) {
      const srcDoc = createSrcDoc(link);
      ret = {
        type: "document",
        srcdoc: () => srcDoc,
        intrinsicSize: { w: 550, h: 720 },
      };
      // assume regular url
    } else {
      ret = {
        type: "document",
        srcdoc: () =>
            createSrcDoc(`
          <script src="${link}.js"></script>
          <style type="text/css">
            * { margin: 0px; }
            table, .gist { height: 100%; }
            .gist .gist-file { height: calc(100vh - 2px); padding: 0px; display: grid; grid-template-rows: 1fr auto; }
          </style>
        `),
        intrinsicSize: { w: 550, h: 720 },
      };
    }
    embeddedLinkCache.set(link, ret);
    return ret;
  }

  embeddedLinkCache.set(link, { link, intrinsicSize: aspectRatio, type });
  return { link, intrinsicSize: aspectRatio, type };
};

export const isIframeLikeOrItsLabel = (
    element: NonDeletedExcalidrawElement,
): Boolean => {
  if (isIframeLikeElement(element)) {
    return true;
  }
  if (element.type === "text") {
    const container = getContainerElement(element);
    if (container && isFrameLikeElement(container)) {
      return true;
    }
  }
  return false;
};

export const createPlaceholderEmbeddableLabel = (
    element: ExcalidrawIframeLikeElement,
): ExcalidrawElement => {
  let text: string;
  if (isIframeElement(element)) {
    text = "IFrame element";
  } else {
    text =
        !element.link || element?.link === "" ? "Empty Web-Embed" : element.link;
  }

  const fontSize = Math.max(
      Math.min(element.width / 2, element.width / text.length),
      element.width / 30,
  );
  const fontFamily = FONT_FAMILY.Helvetica;

  const fontString = getFontString({
    fontSize,
    fontFamily,
  });

  return newTextElement({
    x: element.x + element.width / 2,
    y: element.y + element.height / 2,
    strokeColor:
        element.strokeColor !== "transparent" ? element.strokeColor : "black",
    backgroundColor: "transparent",
    fontFamily,
    fontSize,
    text: wrapText(text, fontString, element.width - 20),
    textAlign: "center",
    verticalAlign: VERTICAL_ALIGN.MIDDLE,
    angle: element.angle ?? 0,
  });
};

export const actionSetEmbeddableAsActiveTool = register({
  name: "setEmbeddableAsActiveTool",
  trackEvent: { category: "toolbar" },
  perform: (elements, appState, _, app) => {
    const nextActiveTool = updateActiveTool(appState, {
      type: "embeddable",
    });

    setCursorForShape(app.canvas, {
      ...appState,
      activeTool: nextActiveTool,
    });

    return {
      elements,
      appState: {
        ...appState,
        activeTool: updateActiveTool(appState, {
          type: "embeddable",
        }),
      },
      commitToHistory: false,
    };
  },
});

const validateHostname = (
    url: string,
    /** using a Set assumes it already contains normalized bare domains */
    allowedHostnames: Set<string> | string,
): boolean => {
  try {
    const { hostname } = new URL(url);

    const bareDomain = hostname.replace(/^www\./, "");
    const bareDomainWithFirstSubdomainWildcarded = bareDomain.replace(
        /^([^.]+)/,
        "*",
    );

    // if (allowedHostnames instanceof Set) {
    //   return (
    //     ALLOWED_DOMAINS.has(bareDomain) ||
    //     ALLOWED_DOMAINS.has(bareDomainWithFirstSubdomainWildcarded)
    //   );
    // }

    // if (bareDomain === allowedHostnames.replace(/^www\./, "")) {
    //   return true;
    // }

    const isValidated =
        // @ts-ignore
        allowedHostnames instanceof Set ||
        bareDomain === allowedHostnames.replace(/^www\./, "");
    if (isValidated) {
      return true;
    }
  } catch (error) {
    // ignore
  }
  return false;
};

export const maybeParseEmbedSrc = (str: string): string => {
  const twitterMatch = str.match(RE_TWITTER_EMBED);
  if (twitterMatch && twitterMatch.length === 2) {
    return twitterMatch[1];
  }

  const gistMatch = str.match(RE_GH_GIST_EMBED);
  if (gistMatch && gistMatch.length === 2) {
    return gistMatch[1];
  }

  if (RE_GIPHY.test(str)) {
    return `https://giphy.com/embed/${RE_GIPHY.exec(str)![1]}`;
  }

  const match = str.match(RE_GENERIC_EMBED);
  if (match && match.length === 2) {
    return match[1];
  }
  return str;
};

export const embeddableURLValidator = (
    url: string | null | undefined,
    validateEmbeddable: ExcalidrawProps["validateEmbeddable"],
): boolean => {
  if (!url) {
    return false;
  }
  if (url.startsWith(RE_IFRAME)) {
    return true;
  }
  if (validateEmbeddable != null) {
    if (typeof validateEmbeddable === "function") {
      const ret = validateEmbeddable(url);
      // if return value is undefined, leave validation to default
      if (typeof ret === "boolean") {
        return ret;
      }
    } else if (typeof validateEmbeddable === "boolean") {
      return validateEmbeddable;
    } else if (validateEmbeddable instanceof RegExp) {
      return validateEmbeddable.test(url);
    } else if (Array.isArray(validateEmbeddable)) {
      for (const domain of validateEmbeddable) {
        if (domain instanceof RegExp) {
          if (url.match(domain)) {
            return true;
          }
        } else if (validateHostname(url, domain)) {
          return true;
        }
      }
      return false;
    }
  }

  return validateHostname(url, ALLOWED_DOMAINS);
};
